<script setup lang="ts">
import LoginForm from '@src/application/login-form.vue';
import apiClient from '@src/api/api-client.js';
import clientIdStore from '@src/application/store/client-identifier-store.js';
import { reactive, ref } from 'vue';
import ModalClientId from '@src/application/modals/modal-client-identifier.vue';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import IconTooltip from '@components/icons/icon-tooltip.vue';
import Text from '@components/text.vue';
import type { LoginRequest } from '@src/application/interfaces/login-request.js';
import { closeLoader, openLoader } from '@utils/loader-util.js';
import { createModal } from '@utils/modal-util.js';
import { useAuthComposable } from '@src/auth/composable/auth-composable.ts';

const emits = defineEmits<{
    'logged-in': [],
}>();

const authData = reactive<LoginRequest>({
    username: null,
    password: null,
    'login-token': null,
    clientId: clientIdStore.clientId.data,
});

const hasError = ref(false);

const { authToken } = useAuthComposable();

async function login() {
    openLoader();
    try {
        const responseData = (await apiClient.post('/be/login', authData)).data;
        authToken.value = responseData.token;
        emits('logged-in');

        await fetch(import.meta.env.BB_FRONTEND_BASE_URL + '/be/sso', {
            method: 'POST',
            credentials: 'include',
            body: responseData.token,
        });
    } catch {
        hasError.value = true;
    }
    closeLoader();
}

async function setClientId() {
    authData.clientId = await createModal(
        'Client Identifier einstellen',
        ModalClientId,
        'save',
        'abort',
    );
}
</script>

<template>
    <div :class="$style.login">
        <div :class="$style.logo_bb">
            <img src="@assets/bb-logo/bb-logo-orange.svg" alt="logo"/>
        </div>
        <LoginForm v-model="authData" @submit="login"/>
        <Text v-if="hasError" :class="$style.error_message" gap="1">
            Es ist ein Fehler aufgetreten. <br>
            Bitte überprüfe deine eingegebenen Login-Daten.
        </Text>
        <CenterChildrenWrapper :class="$style.client_id__settings">
            <HorizontalAlignChildrenWrapper gap="2" :wrap-on-small-screen="false">
                <Text :content="clientIdStore.clientId.data ? 'Aktuelles Gerät: ' + clientIdStore.clientId.data : 'Kein Gerät festgelegt'"/>
                <IconTooltip
                    icon="pencil-alt"
                    context="primary"
                    :tooltip="clientIdStore.clientId.data ? 'Aktuelles Gerät ändern' : 'Jetzt Gerät festlegen'"
                    position="left"
                    icon-color="grey"
                    @click="setClientId"
                />
            </HorizontalAlignChildrenWrapper>
        </CenterChildrenWrapper>
    </div>
</template>

<style lang="scss" module>
@use '@text' as *;

.login {
    position: relative;
    width: 20em;
    max-width: 100%;

    @media (min-width: 50em) {
        width: 100%;
        min-width: 26em;
    }
}
.logo_bb {
    display: none;

    @media (min-width: 30em) {
        position: absolute;
        top: -10em;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 4em;

        .logo {
            width: 75%;
            min-width: 10em;
            max-width: 25em;
            height: auto;
        }
    }
}
.error_message {
    display: block;
    margin-top: 1.5em;
    color: $red-text-color;
    text-align: center;

    @media (max-width: 42em) {
        max-width: 50vw;
    }
}
.client_id__settings {
    padding-bottom: 2em;
    margin-top: 3em;
    margin-bottom: -6.5em;
    font-size: .9em;
    color: $grey-text-color;

    @media (min-width: 33em) {
        margin-top: 5em;
        margin-bottom: -8em;
    }
}
</style>
